import React, { useRef, useState } from 'react';
import { FaPaperclip, FaTimes, FaReply } from 'react-icons/fa';
import { LuCornerDownRight } from 'react-icons/lu';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { Badge, Button } from 'components/common';
import { useForm, Controller } from 'react-hook-form';
import TinymceEditor from "components/common/TinymceEditor";
import { createResponse } from 'redux/crm/project/interactionSlice';
import { formatFileSize, getFileIcon } from "helpers/utils";

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

// Form trả lời. Hai chế độ:
//   - không có parentId: response gốc của topic (nút Reply trên đầu card)
//   - có parentId: reply vào một response, mở inline ngay dưới thread đó
const ResponseForm = ({ open, topic, parentId, replyTo, initialContent, onCancel, onSuccess, compact }) => {
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  // Ref thay cho id cố định: mỗi topic (và mỗi thread) có form riêng, id
  // "attachment-upload" trùng nhau làm nút Attach của form này mở input của form khác.
  const fileInputRef = useRef(null);

  const dispatch = useDispatch();

  const projectId = topic.project?.id;
  const topicId = topic?.id;

  const { control, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: {
      content: initialContent
    }
  });

  const handleSubmitResponse = async (data) => {
    if (!topicId) return;

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('content', data.content);
      if (parentId) formData.append('parent_id', parentId);

      documents.forEach((file, index) => {
        formData.append(`documents[${index}]`, file);
      });

      await dispatch(createResponse({ projectId, topicId, data: formData })).unwrap();

      reset({ content: '' });
      setDocuments([]);
      onSuccess?.();
    } catch {
      // Lỗi đã được toast ở rejected matcher của slice; giữ nguyên nội dung để
      // người dùng gửi lại, không bắt gõ lại cả câu trả lời.
    } finally {
      setUploading(false);
    }
  }

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    // Cho phép chọn lại đúng file vừa gỡ ra.
    e.target.value = '';

    if (files.length === 0) return;

    const validFiles = files.filter(file => {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`File ${file.name} is too large. Maximum size is 100MB.`);
        return false;
      }
      return true;
    });

    setDocuments(prev => [...prev, ...validFiles]);
  };

  const handleRemoveAttachment = (index) => {
    setDocuments(prev => prev.filter((_, i) => i !== index));
  };

  const editorHeight = compact ? { height: 150, minHeight: 120, maxHeight: 480 } : { height: 220, minHeight: 160, maxHeight: 640 };

  return (
    <div className={open ? '' : 'tw-hidden'}>
      <div className="tw-mb-4 tw-bg-white tw-rounded tw-border tw-border-solid tw-border-slate-200">
        <div className={compact ? 'tw-p-3' : 'tw-p-4'}>
          <form onSubmit={handleSubmit(handleSubmitResponse)}>
            {replyTo && (
              <div className="tw-flex tw-items-start tw-gap-2 tw-mb-2 tw-px-2.5 tw-py-1.5 tw-rounded-md tw-bg-primary-50 tw-ring-1 tw-ring-primary-100">
                <LuCornerDownRight size={13} className="tw-text-primary-500 tw-mt-0.5 tw-shrink-0" />
                <div className="tw-min-w-0 tw-flex-grow tw-text-xs tw-text-slate-600">
                  Replying to <strong className="tw-text-slate-800">{replyTo.name}</strong>
                  {replyTo.snippet && (
                    <span className="tw-block tw-truncate tw-text-slate-400">{replyTo.snippet}</span>
                  )}
                </div>
                {onCancel && (
                  <button
                    type="button"
                    onClick={onCancel}
                    aria-label="Cancel reply"
                    className="tw-p-1 tw-border-0 tw-bg-transparent tw-text-slate-400 hover:tw-text-slate-700 tw-cursor-pointer tw-shrink-0"
                  >
                    <FaTimes size={11} />
                  </button>
                )}
              </div>
            )}

            <div className="tw-mb-3">
              {!compact && (
                <label className="tw-block tw-text-sm tw-font-medium tw-text-slate-700 tw-mb-1">Content:</label>
              )}
              <Controller
                name="content"
                control={control}
                rules={{ required: 'Content is required' }}
                render={({ field }) => (
                  <div>
                    {/* Draggable resize handle: replies range from one line to
                        a long technical answer with images. */}
                    <TinymceEditor
                      handleChange={field.onChange}
                      value={field.value || ''}
                      placeholder={replyTo ? `Reply to ${replyTo.name}...` : 'Share your thoughts, feedback, or questions...'}
                      resizable
                      {...editorHeight}
                    />
                    {errors?.content && (
                      <div className="tw-text-red-600 tw-text-xs tw-block tw-mt-2">
                        {errors.content?.message}
                      </div>
                    )}
                  </div>
                )}
              />
            </div>

            {documents.length > 0 && (
              <div className="tw-mb-3">
                <div className="tw-flex tw-flex-wrap tw-gap-2">
                  {documents.map((file, index) => {
                    const FileIcon = getFileIcon(file.name);
                    return (
                      <Badge
                        key={`${file.name}-${index}`}
                        variant="default"
                        className="tw-flex tw-items-center tw-gap-2 tw-p-2"
                      >
                        <FileIcon className="tw-text-blue-600" />
                        <span className="tw-text-xs">{file.name}</span>
                        <span className="tw-text-xs tw-text-slate-500">({formatFileSize(file.size)})</span>
                        <FaTimes
                          className="tw-cursor-pointer tw-text-red-600"
                          onClick={() => handleRemoveAttachment(index)}
                        />
                      </Badge>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="tw-flex tw-justify-between tw-items-center">
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  className="tw-hidden"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.svg,.webp,.zip,.rar,.7z"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  <FaPaperclip className="tw-mr-1" />
                  Attach Files
                </Button>

                {documents.length > 0 && (
                  <span className="tw-ml-2 tw-text-xs tw-text-slate-500">
                    {documents.length} file(s) selected
                  </span>
                )}
              </div>

              <div className="tw-flex tw-items-center tw-gap-2">
                {onCancel && (
                  <Button variant="ghost" size="sm" onClick={onCancel} disabled={uploading}>
                    Cancel
                  </Button>
                )}
                <Button
                  type="submit"
                  size="sm"
                  variant="primary"
                  disabled={uploading}
                  loading={uploading}
                >
                  {uploading ? (
                    'Posting...'
                  ) : (
                    <>
                      <FaReply className="tw-mr-2" />
                      {replyTo ? 'Reply' : 'Post'}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

ResponseForm.propTypes = {
  open: PropTypes.bool.isRequired,
  topic: PropTypes.object.isRequired,
  /** id response gốc khi trả lời vào một response */
  parentId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** { name, snippet } của response đang được trả lời */
  replyTo: PropTypes.shape({ name: PropTypes.string, snippet: PropTypes.string }),
  initialContent: PropTypes.string,
  onCancel: PropTypes.func,
  onSuccess: PropTypes.func,
  compact: PropTypes.bool,
};

ResponseForm.defaultProps = {
  parentId: null,
  replyTo: null,
  initialContent: '',
  onCancel: undefined,
  onSuccess: undefined,
  compact: false,
};

export default ResponseForm;
